<script setup>
import dayjs from 'dayjs'
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useDetectDevice } from '@/composables/device'
import { useISODateRange, buildTimeISO, isoToTime } from '@/composables/date-time'

import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import TimeSelector from '@/components/shared/TimeSelector.vue'

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  schedules: {
    type: Array,
    required: true,
  },
  continueAction: {
    type: Function,
    required: false,
    default: null,
  },
  backAction: {
    type: Function,
    required: false,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'update:schedules'])

const editItemRef = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const { fromTo, onUpdateDateRange } = useISODateRange(editItemRef)

const sortedSchedules = computed(() =>
  [...props.schedules].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
)

const hasScheduleOverlap = computed(() => {
  const slots = sortedSchedules.value.filter(s => s.start_time && s.end_time)
  for (let i = 0; i < slots.length - 1; i++) {
    const aEnd = dayjs(`2000-01-01T${slots[i].end_time}`)
    const bStart = dayjs(`2000-01-01T${slots[i + 1].start_time}`)
    if (bStart.isBefore(aEnd)) return true
  }
  return false
})

const onAddScheduleRow = () => {
  const last = sortedSchedules.value[sortedSchedules.value.length - 1]
  let startTime = webSiteStore.getDefaultStartTime()
  let endTime = webSiteStore.getDefaultEndTime()

  if (last?.end_time) {
    const newStart = dayjs(`2000-01-01T${last.end_time}`).add(1, 'hour')
    startTime = newStart.format('HH:mm')
    if (!endTime || !dayjs(`2000-01-01T${endTime}`).isAfter(newStart)) {
      endTime = newStart.add(1, 'hour').format('HH:mm')
    }
  }

  emit('update:schedules', [
    ...props.schedules,
    { start_time: startTime, end_time: endTime },
  ])
}

const onRemoveSchedule = (schedule) => {
  emit('update:schedules', props.schedules.filter(s => s !== schedule))
}

const onUpdateScheduleField = (schedule, field, val) => {
  const updated = props.schedules.map(s =>
    s === schedule ? { ...s, [field]: isoToTime(val) } : s
  )
  emit('update:schedules', updated)
}
</script>

<template>
  <div class="row q-col-gutter-sm">
    <q-field
      v-model="editItemRef"
      borderless
      :class="isMobile ? 'col-12' : 'col-6'"
    >
      <template #control>
        <q-date
          :model-value="fromTo"
          class="full-width"
          first-day-of-week="1"
          range
          today-btn
          :landscape="!isMobile"
          @update:model-value="onUpdateDateRange"
        />
      </template>
    </q-field>

    <div :class="isMobile ? 'col-12' : 'col-6'">
      <div
        v-for="(schedule, idx) in sortedSchedules"
        :key="idx"
        class="row q-col-gutter-sm items-center q-mb-sm"
      >
        <time-selector
          :model-value="buildTimeISO(null, schedule.start_time)"
          :label="i18n.t('common.startTime')"
          class="col"
          :rules="[() => !!schedule.start_time || i18n.t('common.required')]"
          lazy-rules
          @update:model-value="onUpdateScheduleField(schedule, 'start_time', $event)"
        />
        <time-selector
          :model-value="buildTimeISO(null, schedule.end_time)"
          :label="i18n.t('common.endTime')"
          class="col"
          :min="buildTimeISO(null, schedule.start_time)"
          end
          :rules="[() => !!schedule.end_time || i18n.t('common.required')]"
          lazy-rules
          @update:model-value="onUpdateScheduleField(schedule, 'end_time', $event)"
        />
        <q-btn
          v-if="schedules.length > 1"
          flat
          round
          dense
          icon="mdi-minus-circle-outline"
          color="negative"
          @click="onRemoveSchedule(schedule)"
        />
      </div>
      <q-btn
        flat
        dense
        icon="mdi-plus-circle-outline"
        color="primary"
        :label="i18n.t('common.addTimeSlot')"
        @click="onAddScheduleRow"
      />
      <div v-if="hasScheduleOverlap" class="text-negative text-caption q-mt-xs">
        {{ i18n.t('error.timeSlotOverlap') }}
      </div>
    </div>
  </div>
  <stepper-navigation
    v-if="continueAction"
    :continue="() => !hasScheduleOverlap && continueAction()"
    :back="backAction"
  />
</template>
